/**
 * The remote host (D156): the platform runs the turn; this asks it to.
 *
 * @remarks
 * This is `play: 'server'`. The bundle never arrives; every verb is a
 * `fetch` to `services/play-api` and every answer is validated against the
 * contract before the shell sees it — a Worker that starts sending
 * something else fails loudly here, not silently in a component.
 *
 * The session's identity is two things: the id, which this host remembers
 * (in whatever memory the edge supplies — `localStorage` in the browser)
 * so a reload resumes; and the cookie the Worker set, which the browser
 * carries with `credentials: 'include'` and this code never sees. A
 * remembered id the Worker no longer knows (`404`) is forgotten and a fresh
 * session opened — the resume rule of the local host, over the wire.
 *
 * `fetch` is a parameter, like every other source of the outside world in
 * this package: the tests hand in the Worker's own `handleRequest`.
 *
 * **The Record, over the wire (D241).** A signed-in player's Record is the
 * account's, and the Worker reads and writes it itself. A player who is not
 * signed in keeps it on the DEVICE: this host sends what `recordStorage`
 * holds with every fresh open, and keeps the layers a turn hands back
 * (`recordLayersForDevice`, present only when the Worker had no account to
 * keep them with). `forgetRecord` forgets both (D242): the device's through
 * the adapter, and the account's by asking the Worker — the one that reads
 * the account's copy for this game, whether or not the build can reach the
 * store itself. A forget the Worker did not confirm throws, so the shell
 * never tells a player their Record is clear when it is not.
 *
 * @packageDocumentation
 */

import {
	openSessionRequestSchema,
	PLAY_RECORD_FORGET_PATH,
	playApiErrorSchema,
	type PortChoiceRequest,
	recordForgetRequestSchema,
	type SessionEnvelope,
	sessionEnvelopeSchema,
	type SubmitTurnRequest,
	turnEnvelopeSchema,
	type TurnOutcome,
} from '@orpheus/api-contracts';

import { type OpenedSession, type PlayHost } from './play-host.js';
import { readRecordSafely, type RecordStorageAdapter } from './record-storage.js';

/** Where the remembered session id lives between page loads. */
export interface SessionMemory {
	forget(gameId: string): void;
	recall(gameId: string): string | undefined;
	remember(gameId: string, sessionId: string): void;
}

/** What the remote host needs from its edge. */
export interface RemoteHostOptions {
	/** The Worker's origin, without a trailing slash — `https://play.orpheusquest.com`. */
	readonly baseUrl: string;
	readonly fetch: typeof fetch;
	readonly gameId: string;
	readonly sessionMemory: SessionMemory;
	/** D241: the DEVICE's Record — sent with a fresh open, and where the layers handed back are kept. Absent, nothing is sent or kept. */
	readonly recordStorage?: RecordStorageAdapter;
	/** An explicit seed — `?seed=` — which outranks a remembered session. */
	readonly seed?: number;
}

/** A refusal from the Worker, by the name it gave. */
export class PlayApiRefusal extends Error {
	public readonly status: number;

	public constructor(status: number, reason: string) {
		super(`play-api answered ${status}: ${reason}`);
		this.name = 'PlayApiRefusal';
		this.status = status;
	}
}

/**
 * Create a host that asks the platform to run the game.
 *
 * @param options - {@link RemoteHostOptions}
 * @returns The host
 */
export const createRemoteHost = (options: RemoteHostOptions): PlayHost => {
	const { baseUrl, gameId, sessionMemory } = options;
	let sessionId: string | undefined;

	/** One request to the Worker, with the cookie along, answered as JSON or refused by name. */
	const call = async (path: string, init: { readonly body?: unknown; readonly method: 'GET' | 'POST' }): Promise<unknown> => {
		const response = await options.fetch(`${baseUrl}${path}`, {
			...(init.body === undefined ? {} : { body: JSON.stringify(init.body) }),
			credentials: 'include',
			headers: init.body === undefined ? {} : { 'Content-Type': 'application/json' },
			method: init.method,
		});

		if (!response.ok) {
			const refusal = playApiErrorSchema.safeParse(await response.json().catch(() => undefined));

			throw new PlayApiRefusal(response.status, refusal.success ? refusal.data.error : 'no reason given');
		}

		return response.json();
	};

	const adopt = (envelope: SessionEnvelope, wasResumed: boolean): OpenedSession => {
		sessionId = envelope.sessionId;
		sessionMemory.remember(gameId, envelope.sessionId);

		return {
			game: envelope.game,
			...(envelope.pendingPort === undefined ? {} : { pendingPort: envelope.pendingPort }),
			seed: envelope.seed,
			view: envelope.view,
			wasResumed,
		};
	};

	const openFresh = async (): Promise<OpenedSession> => {
		const deviceRecord = await readRecordSafely(options.recordStorage, gameId);
		// Parsed on the way out as well as in: a game id the door would refuse is refused here, before the round trip.
		const request = openSessionRequestSchema.parse({
			gameId,
			...(deviceRecord.length === 0 ? {} : { record: deviceRecord }),
			...(options.seed === undefined ? {} : { seed: options.seed }),
		});

		return adopt(sessionEnvelopeSchema.parse(await call('/sessions', { body: request, method: 'POST' })), false);
	};

	/** The session this host is speaking for — which exists only once it is open. */
	const sessionIdOrThrow = (): string => {
		if (sessionId === undefined) {
			throw new Error('the session is not open — call open() first');
		}

		return sessionId;
	};

	return {
		// D163: the answer is the session after it — resumed, on whichever version the player chose.
		choosePort: async (choice) => {
			const request: PortChoiceRequest = { choice };

			return adopt(sessionEnvelopeSchema.parse(await call(`/sessions/${sessionIdOrThrow()}/port`, { body: request, method: 'POST' })), true);
		},
		exportTranscript: async () => {
			const response = await options.fetch(`${baseUrl}/sessions/${sessionIdOrThrow()}/export`, { credentials: 'include', method: 'GET' });

			if (!response.ok) {
				throw new PlayApiRefusal(response.status, 'the export was refused');
			}

			return response.text();
		},
		// D242: the device's copy, then the account's — the Worker refuses by name when the store did not forget.
		forgetRecord: async () => {
			// Parsed on the way out, as the open's is: a game id the door would refuse is refused here.
			const request = recordForgetRequestSchema.parse({ gameId });

			await options.recordStorage?.forgetRecord(gameId);
			await call(PLAY_RECORD_FORGET_PATH, { body: request, method: 'POST' });
		},
		open: async () => {
			const remembered = options.seed === undefined ? sessionMemory.recall(gameId) : undefined;

			if (remembered === undefined) {
				return openFresh();
			}

			try {
				return adopt(sessionEnvelopeSchema.parse(await call(`/sessions/${remembered}`, { method: 'GET' })), true);
			} catch (error) {
				// A session the Worker no longer knows — expired, or another browser's
				// — is forgotten and begun afresh. Any other refusal is the caller's.
				if (error instanceof PlayApiRefusal && error.status === 404) {
					sessionMemory.forget(gameId);

					return openFresh();
				}

				throw error;
			}
		},
		restart: async () => openFresh(),
		submit: async (input: string): Promise<TurnOutcome> => {
			const request: SubmitTurnRequest = { input };
			const envelope = turnEnvelopeSchema.parse(await call(`/sessions/${sessionIdOrThrow()}/turns`, { body: request, method: 'POST' }));
			const { recordLayersForDevice } = envelope.outcome;

			// D241: the Worker had no account to keep these with — the device keeps them. A failed write loses that write, never the turn.
			if (recordLayersForDevice !== undefined) {
				await options.recordStorage?.appendLayers(gameId, recordLayersForDevice).catch(() => undefined);
			}

			return envelope.outcome;
		},
	};
};
